import type { GameDefinition, Outcome, Seat, Viewer } from '@campfire/engine';
import { DIAGONALS, between, label, stepFrom, yOf } from '@campfire/grid';

/** Board size and the quiet-move rule: 40 moves per side (80 plies) without a capture or a man move is a draw. */
export const SIZE = 8;
export const QUIET_LIMIT = 80;

/** Square contents: 0 empty, 1/2 dark man/king (seat 0), 3/4 light man/king (seat 1). */
export const EMPTY = 0;
export const DARK_MAN = 1;
export const DARK_KING = 2;
export const LIGHT_MAN = 3;
export const LIGHT_KING = 4;
export const ownerOf = (p: number): Seat | null => (p === EMPTY ? null : (p - 1) >> 1);
export const isKing = (p: number): boolean => p === DARK_KING || p === LIGHT_KING;
export const SEAT_NAMES = ['Dark', 'Light'] as const;

export interface CheckersOptions {
  /** A player who can capture must (a chain, once started, is always completed). */
  readonly forcedCapture: boolean;
}

export interface CheckersState {
  readonly options: CheckersOptions;
  readonly board: readonly number[];
  readonly turn: Seat;
  /** Squares of the last move, start first (every landing square of a chain). */
  readonly last: readonly number[] | null;
  /** Squares of the pieces the last move captured. */
  readonly lastCaptured: readonly number[];
  /** Pieces each seat has captured. */
  readonly captures: readonly [number, number];
  /** Plies since the last capture or man move. */
  readonly quiet: number;
  readonly moves: number;
  /** Seat that has offered a draw the other has not yet answered. */
  readonly drawOffer: Seat | null;
  readonly result: Outcome | null;
}

/**
 * One move is one action carrying the whole path: the start square, then each landing square.
 * A multi-jump is a single action, so a half-finished chain can never be left behind.
 */
export type CheckersAction =
  | { readonly type: 'move'; readonly path: readonly number[] }
  | { readonly type: 'offerDraw' }
  | { readonly type: 'acceptDraw' }
  | { readonly type: 'declineDraw' }
  | { readonly type: 'resign' };

export interface CheckersView {
  readonly you: Seat | null;
  readonly options: CheckersOptions;
  readonly board: readonly number[];
  readonly turn: Seat | null;
  /** Every legal path for the viewer when it is their move; empty otherwise. */
  readonly legal: readonly (readonly number[])[];
  readonly last: readonly number[] | null;
  readonly lastCaptured: readonly number[];
  readonly captures: readonly [number, number];
  readonly remaining: readonly [number, number];
  readonly drawOffer: Seat | null;
  readonly quiet: number;
  readonly moves: number;
  readonly result: Outcome | null;
}

export interface Line {
  readonly path: readonly number[];
  readonly captured: readonly number[];
}

const forward = (seat: Seat): number => (seat === 0 ? 1 : -1);
const crownRow = (seat: Seat): number => (seat === 0 ? SIZE - 1 : 0);
const dirsOf = (p: number) => (isKing(p) ? DIAGONALS : DIAGONALS.filter((d) => d[1] === forward(ownerOf(p) as Seat)));

export function initialBoard(): number[] {
  const b = new Array<number>(SIZE * SIZE).fill(EMPTY);
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if ((x + y) % 2 !== 0) continue; // dark squares only; a1 is dark
      if (y < 3) b[y * SIZE + x] = DARK_MAN;
      else if (y > 4) b[y * SIZE + x] = LIGHT_MAN;
    }
  }
  return b;
}

/** Continues a jump chain from `at`. A jumped piece stays on the board until the move ends but cannot be jumped twice. */
function chains(b: readonly number[], seat: Seat, at: number, piece: number, path: number[], taken: number[], out: Line[]): void {
  let more = false;
  for (const d of dirsOf(piece)) {
    const over = stepFrom(SIZE, at, d);
    const land = stepFrom(SIZE, at, d, 2);
    if (over === null || land === null) continue;
    const t = b[over] as number;
    if (t === EMPTY || ownerOf(t) === seat || taken.includes(over) || b[land] !== EMPTY) continue;
    more = true;
    const np = [...path, land];
    const nt = [...taken, over];
    if (!isKing(piece) && yOf(SIZE, land) === crownRow(seat)) out.push({ path: np, captured: nt }); // crowning ends the move
    else chains(b, seat, land, piece, np, nt, out);
  }
  if (!more && taken.length > 0) out.push({ path, captured: taken });
}

/** Every legal move for `seat`, honouring the forced-capture setting. */
export function legalLines(board: readonly number[], seat: Seat, forced: boolean): Line[] {
  const jumps: Line[] = [];
  const steps: Line[] = [];
  for (let from = 0; from < board.length; from++) {
    const piece = board[from] as number;
    if (ownerOf(piece) !== seat) continue;
    const lifted = board.slice();
    lifted[from] = EMPTY; // the mover may pass back over its own start square
    chains(lifted, seat, from, piece, [from], [], jumps);
    for (const d of dirsOf(piece)) {
      const to = stepFrom(SIZE, from, d);
      if (to !== null && board[to] === EMPTY) steps.push({ path: [from, to], captured: [] });
    }
  }
  return forced && jumps.length > 0 ? jumps : [...jumps, ...steps];
}

const same = (a: readonly number[], b: readonly number[]) => a.length === b.length && a.every((v, i) => v === b[i]);
const other = (s: Seat): Seat => 1 - s;
const count = (board: readonly number[], seat: Seat) => board.filter((p) => ownerOf(p) === seat).length;

/** "c3-e5-g7" style text for a path. */
export const pathText = (path: readonly number[]): string => path.map((i) => label(SIZE, i)).join('-');

const win = (seat: Seat, reason: string, s: CheckersState): Outcome => ({ winners: [seat], draw: false, reason, scores: { 0: 12 - s.captures[1], 1: 12 - s.captures[0] } });
const draw = (reason: string, s: CheckersState): Outcome => ({ winners: [], draw: true, reason, scores: { 0: 12 - s.captures[1], 1: 12 - s.captures[0] } });

export const checkers: GameDefinition<CheckersState, CheckersAction, CheckersView, CheckersOptions> = {
  id: 'checkers',
  name: 'Checkers',
  rules: [
    'American checkers (English draughts) for two players on an 8x8 board. Each side starts with 12 pieces on the dark squares. Dark moves first.',
    'A piece moves one square diagonally onto an empty dark square. Ordinary pieces (men) only move forward. Tap a piece to see where it can go, then tap a highlighted square.',
    'To capture, jump diagonally over an adjacent enemy piece onto the empty square just beyond it. The jumped piece is removed.',
    'After a jump, the same piece must keep jumping if it can. Tap each landing square in turn. The move ends when no further jump is possible. A piece cannot jump the same piece twice in one move.',
    'Captures are compulsory by default: if any capture is available you must make one (you may choose which). Table setting: turn this off to make captures optional. A started chain is still completed.',
    'A man that reaches the far row becomes a king and its move ends there, even in the middle of a chain. Kings move and jump diagonally in both directions, one square at a time.',
    'You win when your opponent has no pieces left or cannot move.',
    'Draw: either player can offer a draw, and the other can accept or decline (making a move declines it). The game is also drawn automatically after 40 moves by each side (80 in all) without a capture or a man move.',
    'You can resign at any time. The app enforces every rule, so spectators see the whole board.',
  ],
  minSeats: 2,
  maxSeats: 2,
  defaultOptions: { forcedCapture: true },
  optionSpecs: [
    {
      key: 'forcedCapture',
      label: 'Captures',
      help: 'Traditional rules make a capture compulsory.',
      choices: [
        { value: true, label: 'Compulsory' },
        { value: false, label: 'Optional' },
      ],
    },
  ],

  setup: ({ options }) => ({
    options,
    board: initialBoard(),
    turn: 0,
    last: null,
    lastCaptured: [],
    captures: [0, 0],
    quiet: 0,
    moves: 0,
    drawOffer: null,
    result: null,
  }),

  // Either player may resign or answer a draw offer at any time; only the mover's move is due.
  activeSeats: (s) => (s.result ? [] : [0, 1]),
  waitingOn: (s) => (s.result ? [] : [s.turn]),

  validate(s, a, seat) {
    if (s.result) return { ok: false, reason: 'game over' };
    if (seat !== 0 && seat !== 1) return { ok: false, reason: 'not a player' };
    switch (a.type) {
      case 'move': {
        if (seat !== s.turn) return { ok: false, reason: 'not your turn' };
        if (!Array.isArray(a.path) || a.path.length < 2 || !a.path.every((i) => Number.isInteger(i))) return { ok: false, reason: 'a move is a list of squares' };
        const lines = legalLines(s.board, seat, s.options.forcedCapture);
        if (lines.some((l) => same(l.path, a.path))) return { ok: true };
        const piece = s.board[a.path[0] as number];
        if (piece === undefined || ownerOf(piece) !== seat) return { ok: false, reason: 'start from one of your pieces' };
        if (lines.some((l) => l.captured.length > 0 && l.path[0] === a.path[0] && l.path.length > a.path.length && same(l.path.slice(0, a.path.length), a.path))) {
          return { ok: false, reason: 'keep jumping: finish the chain' };
        }
        if (s.options.forcedCapture && lines.length > 0 && (lines[0] as Line).captured.length > 0) return { ok: false, reason: 'you must capture' };
        return { ok: false, reason: 'illegal move' };
      }
      case 'offerDraw':
        if (s.drawOffer === seat) return { ok: false, reason: 'you already offered a draw' };
        return s.drawOffer === null ? { ok: true } : { ok: false, reason: 'your opponent has offered a draw: accept or decline' };
      case 'acceptDraw':
      case 'declineDraw':
        return s.drawOffer === other(seat) ? { ok: true } : { ok: false, reason: 'no draw offer to answer' };
      case 'resign':
        return { ok: true };
      default:
        return { ok: false, reason: 'unknown action' };
    }
  },

  apply(s, a, ctx) {
    const seat = ctx.actor as Seat;
    if (a.type === 'resign') return { ...s, drawOffer: null, result: win(other(seat), `${SEAT_NAMES[seat]} resigned`, s) };
    if (a.type === 'offerDraw') return { ...s, drawOffer: seat };
    if (a.type === 'declineDraw') return { ...s, drawOffer: null };
    if (a.type === 'acceptDraw') return { ...s, drawOffer: null, result: draw('draw agreed', s) };

    const line = legalLines(s.board, seat, s.options.forcedCapture).find((l) => same(l.path, a.path)) as Line;
    const board = s.board.slice();
    const from = line.path[0] as number;
    const to = line.path[line.path.length - 1] as number;
    const piece = board[from] as number;
    board[from] = EMPTY;
    for (const c of line.captured) board[c] = EMPTY;
    board[to] = !isKing(piece) && yOf(SIZE, to) === crownRow(seat) ? piece + 1 : piece;
    const gained = line.captured.length;
    const captures: [number, number] = seat === 0 ? [s.captures[0] + gained, s.captures[1]] : [s.captures[0], s.captures[1] + gained];
    const next: CheckersState = {
      ...s,
      board,
      turn: other(seat),
      last: line.path,
      lastCaptured: line.captured,
      captures,
      quiet: line.captured.length > 0 || !isKing(piece) ? 0 : s.quiet + 1,
      moves: s.moves + 1,
      // Moving is an answer: an offer made to the mover is declined.
      drawOffer: s.drawOffer === other(seat) ? null : s.drawOffer,
    };
    if (count(board, other(seat)) === 0) return { ...next, result: win(seat, 'captured every piece', next) };
    if (legalLines(board, other(seat), s.options.forcedCapture).length === 0) return { ...next, result: win(seat, 'opponent cannot move', next) };
    if (next.quiet >= QUIET_LIMIT) return { ...next, result: draw('40 moves each with no capture or man move', next) };
    return next;
  },

  view(s, viewer: Viewer): CheckersView {
    const you = viewer.kind === 'seat' ? viewer.seat : null;
    return {
      you,
      options: s.options,
      board: s.board,
      turn: s.result ? null : s.turn,
      legal: you !== null && you === s.turn && !s.result ? legalLines(s.board, you, s.options.forcedCapture).map((l) => l.path) : [],
      last: s.last,
      lastCaptured: s.lastCaptured,
      captures: s.captures,
      remaining: [count(s.board, 0), count(s.board, 1)],
      drawOffer: s.drawOffer,
      quiet: s.quiet,
      moves: s.moves,
      result: s.result,
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (s.result) return [];
    const out: CheckersAction[] = [];
    if (seat === s.turn) for (const l of legalLines(s.board, seat, s.options.forcedCapture)) out.push({ type: 'move', path: l.path });
    if (s.drawOffer === null) out.push({ type: 'offerDraw' });
    else if (s.drawOffer !== seat) out.push({ type: 'acceptDraw' }, { type: 'declineDraw' });
    out.push({ type: 'resign' });
    return out;
  },
};

/** Squares a path jumped over (a one-square step jumps nothing). */
export const jumpedSquares = (path: readonly number[]): number[] =>
  path.slice(1).flatMap((to, i) => {
    const m = between(SIZE, path[i] as number, to);
    return m === null ? [] : [m];
  });
